import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Pill } from '../components/Pill';
import { Modal } from '../components/Modal';
import { Ic } from '../components/Icon';
import { AdminTabs } from '../components/AdminTabs';
import { adminApi, type AdminInvitationDto } from '../api/admin';
import type { HttpError } from '../api/client';
import { formatDate } from '../lib/date';

export function AdminInvitationsPage() {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['admin-invitations'], queryFn: adminApi.listInvitations });
  const [toDelete, setToDelete] = useState<AdminInvitationDto | null>(null);
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-invitations'] });
  const onError = (err: unknown) => setActionError((err as HttpError)?.body?.message ?? 'Akcja nie powiodła się.');

  const del = useMutation({
    mutationFn: (id: string) => adminApi.deleteInvitation(id),
    onSuccess: () => {
      setToDelete(null);
      invalidate();
    },
    onError,
  });
  const cleanup = useMutation({
    mutationFn: adminApi.cleanupExpiredInvitations,
    onSuccess: () => {
      setConfirmCleanup(false);
      invalidate();
    },
    onError,
  });

  const all = list.data ?? [];
  const expiredCount = all.filter((i) => i.expired && !i.used).length;

  return (
    <DesktopShell>
      <TopBar title="Panel admina" subtitle="Zarządzanie zaproszeniami" />
      <AdminTabs />
      <div className="responsive-pad" style={{ flex: 1, padding: '0 36px 40px' }}>
        {actionError && (
          <Card padding={12} style={{ background: 'rgba(208,70,60,.08)', color: '#8a2a22', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13 }}>{actionError}</span>
              <button
                onClick={() => setActionError(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#8a2a22' }}
              >
                <Ic.x size={14} />
              </button>
            </div>
          </Card>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12 }}>
          <h3 className="serif" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            Wszystkie zaproszenia <span style={{ color: '#5b6578', fontWeight: 500 }}>· {all.length}</span>
          </h3>
          <Btn
            variant="ghost"
            size="sm"
            icon={<Ic.trash size={12} />}
            onClick={() => setConfirmCleanup(true)}
            disabled={expiredCount === 0}
          >
            Usuń wygasłe ({expiredCount})
          </Btn>
        </div>

        {list.isLoading && <Card padding={20}>Ładowanie…</Card>}

        {all.length > 0 && (
          <Card padding={0}>
            {all.map((inv, i) => (
              <div
                key={`${inv.type}-${inv.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 22px',
                  borderBottom: i < all.length - 1 ? '1px solid rgba(15,30,61,.06)' : 'none',
                  background: inv.expired || inv.used ? 'rgba(15,30,61,.02)' : 'transparent',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Pill color={inv.type === 'ROOM_USER' ? 'accent' : 'gray'}>
                      {inv.type === 'ROOM_USER' ? 'PO NICKU' : 'EMAIL/LINK'}
                    </Pill>
                    {inv.code && (
                      <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{inv.code}</span>
                    )}
                    {inv.used && <Pill color="green">zaakceptowany</Pill>}
                    {!inv.used && inv.expired && <Pill color="live">wygasły</Pill>}
                    {!inv.used && !inv.expired && <Pill color="gray">aktywny</Pill>}
                  </div>
                  <div style={{ fontSize: 12, color: '#5b6578', marginTop: 4 }}>
                    {inv.roomName ? <><b>{inv.roomName}</b> · </> : null}
                    {inv.type === 'ROOM_USER' ? (
                      <>
                        <b>{inv.invitedByNick ?? '—'}</b>
                        {' zaprosił '}
                        <b>{inv.inviteeNick ?? '—'}</b>
                      </>
                    ) : inv.email ? (
                      <>
                        <b>{inv.invitedByNick ?? '—'}</b>
                        {' wysłał zaproszenie na '}
                        {inv.email}
                      </>
                    ) : (
                      <>
                        <b>{inv.invitedByNick ?? '—'}</b>
                        {' wygenerował · link otwarty'}
                      </>
                    )}
                    {' · wygasa '}{formatDate(inv.expiresAt)}
                  </div>
                </div>
                <Btn
                  variant="danger"
                  size="sm"
                  icon={<Ic.trash size={12} />}
                  onClick={() => setToDelete(inv)}
                >
                  Usuń
                </Btn>
              </div>
            ))}
          </Card>
        )}

        {!list.isLoading && all.length === 0 && (
          <Card padding={24} style={{ textAlign: 'center', color: '#5b6578' }}>
            Brak zaproszeń.
          </Card>
        )}
      </div>

      <Modal
        open={!!toDelete}
        onClose={() => !del.isPending && setToDelete(null)}
        title="Usunąć zaproszenie?"
        footer={
          <>
            <Btn variant="ghost" size="md" onClick={() => setToDelete(null)} disabled={del.isPending}>
              Anuluj
            </Btn>
            <Btn
              variant="danger"
              size="md"
              icon={<Ic.trash size={14} />}
              onClick={() => toDelete && del.mutate(toDelete.id)}
              disabled={del.isPending}
            >
              {del.isPending ? 'Usuwanie…' : 'Usuń'}
            </Btn>
          </>
        }
      >
        {toDelete && (
          <p style={{ margin: 0 }}>
            Kod <strong>{toDelete.code}</strong>{toDelete.roomName ? ` (${toDelete.roomName})` : ''} zostanie usunięty.
            {toDelete.used && ' Zaproszenie zostało już użyte — usunięcie nie wpłynie na konto, które z niego skorzystało.'}
          </p>
        )}
      </Modal>

      <Modal
        open={confirmCleanup}
        onClose={() => !cleanup.isPending && setConfirmCleanup(false)}
        title="Usunąć wszystkie wygasłe zaproszenia?"
        footer={
          <>
            <Btn variant="ghost" size="md" onClick={() => setConfirmCleanup(false)} disabled={cleanup.isPending}>
              Anuluj
            </Btn>
            <Btn
              variant="danger"
              size="md"
              icon={<Ic.trash size={14} />}
              onClick={() => cleanup.mutate()}
              disabled={cleanup.isPending}
            >
              {cleanup.isPending ? 'Usuwanie…' : `Usuń wszystkie (${expiredCount})`}
            </Btn>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Z bazy zostanie wyczyszczonych <strong>{expiredCount}</strong> zaproszeń, których data ważności już minęła.
        </p>
      </Modal>
    </DesktopShell>
  );
}
